// External Libraries
import React from "react";
import moment from "moment";

// Services
import BankReconciliationService from "../../../../services/BankReconciliationService";
import PlaidService from "../../../../services/PlaidService";
import ClientService from "../../../../services/ClientService";

// Common Components
import WComponent from "../../../common/WComponent";
import Button from "../../../common/button/Button";
import DateSelector from "../../../common/date_selector/DateSelector";
import Loader from "../../../common/loader/Loader";

// Sub-components
import DataQualityPanel from "./DataQualityPanel";

// Styling
import "./bank-reconciliation-tool.css";
import "./data-quality-panel.css";

const TABS = {
	summary: "summary",
	unmatchedBank: "unmatchedBank",
	unmatchedGL: "unmatchedGL",
	journalEntries: "journalEntries",
	dataQuality: "dataQuality",
	history: "history"
};

class BankReconciliationTool extends WComponent {
	constructor(props) {
		super(props);

		this.state = {
			// Input State
			source: "pdf", // "pdf" or "plaid"
			startDate: moment().startOf("month").toDate(),
			endDate: new Date(),
			selectedAccountId: null,
			selectedAccountName: "",
			bankStatementFile: null,
			bankStatementEndingBalance: "",
			selectedPlaidAccountId: null,
			selectedPlaidAccountName: "",

			// Data State
			bankAccounts: [],
			plaidAccounts: [],
			plaidConnected: false,
			reconciliationResult: null,
			reconciliationHistory: [],
			clientName: "",

			// Data Quality State
			dataQualityAlerts: [],
			duplicateGroups: [],
			foreignTransactions: [],
			dqBannerDismissed: false,

			// UI State
			loading: false,
			loadingAccounts: true,
			activeTab: TABS.summary,
			error: null
		};
	}

	async componentDidMount() {
		let { clientId } = this.props;

		try {
			const [accounts, history, clientData, plaidConnected, dataQualityAlerts] = await Promise.all([
				BankReconciliationService.fetchBankAccounts({ clientId }),
				BankReconciliationService.listReconciliations({ clientId }),
				ClientService.fetchClient({ clientId }),
				PlaidService.isPlaidAccountConnected({ clientId }),
				BankReconciliationService.fetchDataQualityAlerts({ clientId })
			]);

			let plaidAccounts = [];
			if (plaidConnected) {
				plaidAccounts = await PlaidService.fetchAccounts({ clientId }) || [];
			}

			await this.update({
				dataQualityAlerts: dataQualityAlerts || [],
				bankAccounts: accounts,
				plaidAccounts,
				plaidConnected,
				reconciliationHistory: history,
				clientName: clientData?.client?.name || "",
				loadingAccounts: false
			});
		} catch (error) {
			await this.update({ loadingAccounts: false, error: "Failed to load bank accounts" });
		}
	}

	onSourceChange = async source => {
		await this.update({ source });
	};

	onPlaidAccountChange = async e => {
		const plaidAccountId = e.target.value;
		const account = this.state.plaidAccounts.find(a => a.id === plaidAccountId);
		await this.update({
			selectedPlaidAccountId: plaidAccountId,
			selectedPlaidAccountName: account ? account.name : ""
		});
	};

	onAccountChange = async e => {
		const accountId = e.target.value;
		const account = this.state.bankAccounts.find(a => a.id === parseInt(accountId));
		await this.update({
			selectedAccountId: accountId,
			selectedAccountName: account ? account.name : ""
		});
	};

	onStartDateChange = async startDate => {
		await this.update({ startDate });
	};

	onEndDateChange = async endDate => {
		await this.update({ endDate });
	};

	onFileChange = async e => {
		const file = e.target.files[0] || null;
		await this.update({ bankStatementFile: file });
	};

	onFileRemove = async () => {
		await this.update({ bankStatementFile: null });
		// Reset the file input so the same file can be re-selected
		if (this.fileInputRef) {
			this.fileInputRef.value = "";
		}
	};

	onEndingBalanceChange = async e => {
		await this.update({ bankStatementEndingBalance: e.target.value });
	};

	onReconcileClicked = async e => {
		e.preventDefault();
		let { clientId } = this.props;
		let { source, selectedAccountId, selectedAccountName, startDate, endDate, bankStatementFile, bankStatementEndingBalance, selectedPlaidAccountId, selectedPlaidAccountName } = this.state;

		if (!selectedAccountId) {
			await this.update({ error: "Please select a QBO bank account" });
			return;
		}

		if (source === "plaid" && !selectedPlaidAccountId) {
			await this.update({ error: "Please select a Plaid bank account" });
			return;
		}

		if (source === "pdf" && !bankStatementFile) {
			await this.update({ error: "Please upload a bank statement PDF" });
			return;
		}

		await this.update({ loading: true, error: null, reconciliationResult: null });

		const params = {
			clientId,
			accountId: selectedAccountId,
			accountName: selectedAccountName,
			startDate: moment(startDate).format("YYYY-MM-DD"),
			endDate: moment(endDate).format("YYYY-MM-DD"),
			source
		};

		if (source === "plaid") {
			params.plaidAccountId = selectedPlaidAccountId;
		} else {
			params.file = bankStatementFile;
			params.bankStatementEndingBalance = bankStatementEndingBalance ? parseFloat(bankStatementEndingBalance) : null;
		}

		const result = await BankReconciliationService.runReconciliation(params);

		if (result.error) {
			await this.update({ loading: false, error: result.error });
			return;
		}

		// Refresh history and data quality alerts
		const [history, dataQualityAlerts] = await Promise.all([
			BankReconciliationService.listReconciliations({ clientId }),
			BankReconciliationService.fetchDataQualityAlerts({ clientId })
		]);

		// Extract foreign currency transactions from the reconciliation result
		const allTxns = [
			...(result.unmatchedBankItems || []),
			...(result.matchedItems || [])
		];
		const foreignTransactions = allTxns.filter(t => t.isForeignCurrency);

		await this.update({
			loading: false,
			reconciliationResult: result,
			reconciliationHistory: history,
			duplicateGroups: result.duplicateGroups || [],
			foreignTransactions,
			dataQualityAlerts: dataQualityAlerts || [],
			dqBannerDismissed: false,
			activeTab: TABS.summary
		});
	};

	onDownloadClicked = async () => {
		let { reconciliationResult, clientName, endDate } = this.state;
		if (!reconciliationResult) return;

		await BankReconciliationService.downloadReconciliation({
			reconciliationId: reconciliationResult.reconciliation.id,
			clientName,
			periodEnd: moment(endDate).format("YYYY-MM-DD")
		});
	};

	onHistoryItemClicked = async reconciliationId => {
		await this.update({ loading: true });

		const reconciliation = await BankReconciliationService.getReconciliation({ reconciliationId });

		if (reconciliation) {
			// Reshape into the same format as runReconciliation result for display
			const items = reconciliation.items || [];
			const adjustments = reconciliation.adjustments || [];

			const result = {
				reconciliation: {
					id: reconciliation.id,
					clientId: reconciliation.client_id,
					accountName: reconciliation.account_name,
					periodStart: reconciliation.period_start,
					periodEnd: reconciliation.period_end,
					status: reconciliation.status
				},
				summary: {
					bankEndingBalance: parseFloat(reconciliation.bank_statement_ending_balance),
					glClosingBalance: parseFloat(reconciliation.gl_closing_balance),
					adjustedBankBalance: parseFloat(reconciliation.adjusted_bank_balance),
					adjustedBookBalance: parseFloat(reconciliation.adjusted_book_balance),
					difference: parseFloat(reconciliation.difference),
					outstandingDeposits: items
						.filter(i => i.category === "outstanding_deposit")
						.reduce((sum, i) => sum + parseFloat(i.amount), 0),
					outstandingCheques: items
						.filter(i => i.category === "outstanding_cheque")
						.reduce((sum, i) => sum + Math.abs(parseFloat(i.amount)), 0),
					bankInterest: items
						.filter(i => i.category === "bank_interest")
						.reduce((sum, i) => sum + parseFloat(i.amount), 0),
					bankCharges: items
						.filter(i => i.category === "bank_charge")
						.reduce((sum, i) => sum + Math.abs(parseFloat(i.amount)), 0),
					nsfFees: items
						.filter(i => i.category === "nsf_fee")
						.reduce((sum, i) => sum + Math.abs(parseFloat(i.amount)), 0)
				},
				matchingStats: {
					totalBankTransactions: items.filter(i => i.source === "bank").length,
					totalGLEntries: items.filter(i => i.source === "gl").length,
					exactMatches: items.filter(i => i.match_status === "matched" && !i.match_confidence).length / 2,
					fuzzyMatches: items.filter(i => i.match_status === "matched" && i.match_confidence).length / 2,
					aiMatches: items.filter(i => i.match_status === "ai_matched").length / 2,
					unmatchedBankItems: items.filter(i => i.source === "bank" && i.match_status === "unmatched").length,
					unmatchedGLItems: items.filter(i => i.source === "gl" && i.match_status === "unmatched").length
				},
				unmatchedBankItems: items
					.filter(i => i.source === "bank" && i.match_status === "unmatched")
					.map(i => ({
						date: i.txn_date,
						type: i.txn_type,
						docNum: i.doc_num,
						name: i.name,
						description: i.description,
						amount: parseFloat(i.amount),
						category: i.category
					})),
				unmatchedGLItems: items
					.filter(i => i.source === "gl" && i.match_status === "unmatched")
					.map(i => ({
						date: i.txn_date,
						type: i.txn_type,
						docNum: i.doc_num,
						name: i.name,
						description: i.description,
						amount: parseFloat(i.amount),
						category: i.category
					})),
				journalEntrySuggestions: adjustments.map(a => ({
					id: a.id,
					debitAccount: a.debit_account,
					creditAccount: a.credit_account,
					amount: parseFloat(a.amount),
					memo: a.memo,
					status: a.status || "suggested",
					sourceTxnId: a.source_txn_id || null
				}))
			};

			await this.update({
				loading: false,
				reconciliationResult: result,
				activeTab: TABS.summary
			});
		} else {
			await this.update({ loading: false, error: "Failed to load reconciliation" });
		}
	};

	onTabClick = async tab => {
		await this.update({ activeTab: tab });
	};

	onAcceptAdjustment = async adjustmentId => {
		const result = await BankReconciliationService.updateAdjustmentStatus({
			adjustmentId,
			status: "accepted"
		});

		if (result && !result.error) {
			this._updateAdjustmentInState(adjustmentId, "accepted");
		}
	};

	onRejectAdjustment = async adjustmentId => {
		const result = await BankReconciliationService.updateAdjustmentStatus({
			adjustmentId,
			status: "rejected"
		});

		if (result && !result.error) {
			this._updateAdjustmentInState(adjustmentId, "rejected");
		}
	};

	onPostAdjustment = async adjustmentId => {
		let { clientId } = this.props;

		this._updateAdjustmentInState(adjustmentId, "posting");

		const result = await BankReconciliationService.postAdjustmentToQBO({
			adjustmentId,
			clientId
		});

		if (result && !result.error) {
			let postedDebitAccount = null;
			let postedCreditAccount = null;

			if (result.journalEntry && result.journalEntry.Line) {
				result.journalEntry.Line.forEach(line => {
					const detail = line.JournalEntryLineDetail;
					if (detail && detail.AccountRef) {
						if (detail.PostingType === "Debit") postedDebitAccount = detail.AccountRef.name;
						else if (detail.PostingType === "Credit") postedCreditAccount = detail.AccountRef.name;
					}
				});
			}

			this._updateAdjustmentInState(adjustmentId, "posted", { postedDebitAccount, postedCreditAccount });
		} else {
			this._updateAdjustmentInState(adjustmentId, "accepted");
			await this.update({ error: result.error || "Failed to post journal entry" });
		}
	};

	_updateAdjustmentInState(adjustmentId, newStatus, extraData) {
		let { reconciliationResult } = this.state;
		if (!reconciliationResult) return;

		const updated = {
			...reconciliationResult,
			journalEntrySuggestions: reconciliationResult.journalEntrySuggestions.map(je =>
				je.id === adjustmentId ? { ...je, status: newStatus, ...(extraData || {}) } : je
			)
		};

		this.update({ reconciliationResult: updated });
	}

	onMemoEditStart = (adjustmentId, currentMemo) => {
		this.update({ editingMemoId: adjustmentId, editingMemoValue: currentMemo || "" });
	};

	onMemoEditChange = e => {
		this.update({ editingMemoValue: e.target.value });
	};

	onMemoEditSave = async adjustmentId => {
		let { editingMemoValue, reconciliationResult } = this.state;

		const result = await BankReconciliationService.updateAdjustmentStatus({
			adjustmentId,
			memo: editingMemoValue
		});

		if (result && !result.error) {
			const updated = {
				...reconciliationResult,
				journalEntrySuggestions: reconciliationResult.journalEntrySuggestions.map(je =>
					je.id === adjustmentId ? { ...je, memo: editingMemoValue } : je
				)
			};
			this.update({ reconciliationResult: updated, editingMemoId: null, editingMemoValue: "" });
		}
	};

	onMemoEditCancel = () => {
		this.update({ editingMemoId: null, editingMemoValue: "" });
	};

	onMemoKeyDown = (e, adjustmentId) => {
		if (e.key === "Enter") {
			this.onMemoEditSave(adjustmentId);
		} else if (e.key === "Escape") {
			this.onMemoEditCancel();
		}
	};

	formatCurrency(amount, currencyCode) {
		const cur = (currencyCode || "CAD").toUpperCase();
		try {
			return new Intl.NumberFormat("en-CA", {
				style: "currency",
				currency: cur,
				currencyDisplay: "code"
			}).format(amount || 0);
		} catch (e) {
			return `${cur} ${(amount || 0).toFixed(2)}`;
		}
	}

	renderControls() {
		let { bankAccounts, selectedAccountId, startDate, endDate, loadingAccounts, bankStatementFile, bankStatementEndingBalance, loading, source, plaidAccounts, plaidConnected, selectedPlaidAccountId } = this.state;

		return (
			<div className="bank-recon-tool__controls-wrap">
				{/* Source Toggle */}
				<div className="bank-recon-tool__source-toggle">
					<label>Bank Statement Source</label>
					<div className="bank-recon-tool__source-buttons">
						<button
							className={`bank-recon-tool__source-btn ${source === "pdf" ? "bank-recon-tool__source-btn--active" : ""}`}
							onClick={() => this.onSourceChange("pdf")}
						>
							Upload PDF
						</button>
						<button
							className={`bank-recon-tool__source-btn ${source === "plaid" ? "bank-recon-tool__source-btn--active" : ""}`}
							onClick={() => this.onSourceChange("plaid")}
							disabled={!plaidConnected}
							title={!plaidConnected ? "No Plaid bank account connected" : ""}
						>
							Plaid Account
						</button>
					</div>
				</div>

				<div className="bank-recon-tool__controls">
					<div className="bank-recon-tool__account-select">
						<label>QBO Bank Account (Books)</label>
						<select value={selectedAccountId || ""} onChange={this.onAccountChange} disabled={loadingAccounts}>
							<option value="">{loadingAccounts ? "Loading accounts..." : "Select a QBO bank account"}</option>
							{bankAccounts.map(account => (
								<option key={account.id} value={account.id}>
									{account.name} ({this.formatCurrency(account.balance)})
								</option>
							))}
						</select>
					</div>

					<DateSelector title="Start Date" selectedDate={startDate ? new Date(startDate) : undefined} onDateSelect={this.onStartDateChange} />
					<DateSelector title="End Date" selectedDate={endDate ? new Date(endDate) : undefined} onDateSelect={this.onEndDateChange} />
				</div>

				<div className="bank-recon-tool__controls">
					{source === "plaid" ? (
						<div className="bank-recon-tool__account-select">
							<label>Plaid Bank Account (Statement)</label>
							<select value={selectedPlaidAccountId || ""} onChange={this.onPlaidAccountChange}>
								<option value="">Select a Plaid account</option>
								{plaidAccounts.map(account => (
									<option key={account.id} value={account.id}>
										{account.name}
									</option>
								))}
							</select>
						</div>
					) : (
						<>
							<div className="bank-recon-tool__file-upload">
								<label>Bank Statement (PDF)</label>
								<div className="bank-recon-tool__file-upload-row">
									<label className="bank-recon-tool__file-btn" htmlFor="bank-statement-upload">
										{bankStatementFile ? "Change File" : "Upload PDF"}
										<input
											id="bank-statement-upload"
											type="file"
											accept=".pdf"
											onChange={this.onFileChange}
											ref={ref => (this.fileInputRef = ref)}
											style={{ display: "none" }}
										/>
									</label>
									{bankStatementFile && (
										<div className="bank-recon-tool__file-info">
											<span className="bank-recon-tool__file-name">{bankStatementFile.name}</span>
											<span className="bank-recon-tool__file-remove" onClick={this.onFileRemove}>
												&times;
											</span>
										</div>
									)}
								</div>
							</div>

							<div className="bank-recon-tool__ending-balance">
								<label>Ending Balance (optional override)</label>
								<input
									type="number"
									step="0.01"
									placeholder="Auto-detected from PDF"
									value={bankStatementEndingBalance}
									onChange={this.onEndingBalanceChange}
								/>
							</div>
						</>
					)}

					<Button text="Reconcile" onClick={this.onReconcileClicked} disabled={loading} />
				</div>
			</div>
		);
	}

	renderTabs() {
		let { activeTab, reconciliationResult } = this.state;
		if (!reconciliationResult) return null;

		return (
			<div className="bank-recon-tabs">
				<div className={`bank-recon-tabs__tab ${activeTab === TABS.summary ? "bank-recon-tabs__tab--active" : ""}`} onClick={() => this.onTabClick(TABS.summary)}>
					Summary
				</div>
				<div className={`bank-recon-tabs__tab ${activeTab === TABS.unmatchedBank ? "bank-recon-tabs__tab--active" : ""}`} onClick={() => this.onTabClick(TABS.unmatchedBank)}>
					Bank-Only Items ({reconciliationResult.unmatchedBankItems?.length || 0})
				</div>
				<div className={`bank-recon-tabs__tab ${activeTab === TABS.unmatchedGL ? "bank-recon-tabs__tab--active" : ""}`} onClick={() => this.onTabClick(TABS.unmatchedGL)}>
					GL-Only Items ({reconciliationResult.unmatchedGLItems?.length || 0})
				</div>
				<div className={`bank-recon-tabs__tab ${activeTab === TABS.journalEntries ? "bank-recon-tabs__tab--active" : ""}`} onClick={() => this.onTabClick(TABS.journalEntries)}>
					Journal Entries ({reconciliationResult.journalEntrySuggestions?.length || 0})
				</div>
				<div className={`bank-recon-tabs__tab ${activeTab === TABS.dataQuality ? "bank-recon-tabs__tab--active" : ""}`} onClick={() => this.onTabClick(TABS.dataQuality)}>
					Data Quality ({(this.state.duplicateGroups?.length || 0) + (this.state.dataQualityAlerts?.filter(a => a.status !== "resolved").length || 0) + (this.state.foreignTransactions?.length || 0)})
				</div>
				<div className={`bank-recon-tabs__tab ${activeTab === TABS.history ? "bank-recon-tabs__tab--active" : ""}`} onClick={() => this.onTabClick(TABS.history)}>
					History
				</div>
			</div>
		);
	}

	renderSummary() {
		let { reconciliationResult } = this.state;
		if (!reconciliationResult) return null;
		let { summary, matchingStats } = reconciliationResult;

		const diffClass = summary.difference === 0 ? "bank-recon-summary__row--zero" : "bank-recon-summary__row--nonzero";

		return (
			<>
				{/* Matching Stats */}
				<div className="bank-recon-stats">
					<div className="bank-recon-stats__badge">
						<div className="bank-recon-stats__badge-label">Bank Txns</div>
						<div className="bank-recon-stats__badge-value">{matchingStats.totalBankTransactions}</div>
					</div>
					<div className="bank-recon-stats__badge">
						<div className="bank-recon-stats__badge-label">GL Entries</div>
						<div className="bank-recon-stats__badge-value">{matchingStats.totalGLEntries}</div>
					</div>
					<div className="bank-recon-stats__badge">
						<div className="bank-recon-stats__badge-label">Exact Matches</div>
						<div className="bank-recon-stats__badge-value">{matchingStats.exactMatches}</div>
					</div>
					<div className="bank-recon-stats__badge">
						<div className="bank-recon-stats__badge-label">Fuzzy Matches</div>
						<div className="bank-recon-stats__badge-value">{matchingStats.fuzzyMatches}</div>
					</div>
					<div className="bank-recon-stats__badge">
						<div className="bank-recon-stats__badge-label">AI Matches</div>
						<div className="bank-recon-stats__badge-value">{matchingStats.aiMatches}</div>
					</div>
					<div className="bank-recon-stats__badge">
						<div className="bank-recon-stats__badge-label">Unmatched</div>
						<div className="bank-recon-stats__badge-value">
							{matchingStats.unmatchedBankItems + matchingStats.unmatchedGLItems}
						</div>
					</div>
				</div>

				{/* Reconciliation Summary Cards */}
				<div className="bank-recon-summary">
					{/* Bank Side */}
					<div className="bank-recon-summary__card">
						<h3>Bank Side</h3>
						<div className="bank-recon-summary__row">
							<span>Bank Statement Ending Balance</span>
							<span>{this.formatCurrency(summary.bankEndingBalance)}</span>
						</div>
						<div className="bank-recon-summary__row">
							<span>Add: Outstanding Deposits</span>
							<span>{this.formatCurrency(summary.outstandingDeposits)}</span>
						</div>
						<div className="bank-recon-summary__row">
							<span>Less: Outstanding Cheques</span>
							<span>({this.formatCurrency(summary.outstandingCheques)})</span>
						</div>
						<div className="bank-recon-summary__row bank-recon-summary__row--total">
							<span>Adjusted Bank Balance</span>
							<span>{this.formatCurrency(summary.adjustedBankBalance)}</span>
						</div>
					</div>

					{/* Book Side */}
					<div className="bank-recon-summary__card">
						<h3>Book Side</h3>
						<div className="bank-recon-summary__row">
							<span>GL Closing Balance (per books)</span>
							<span>{this.formatCurrency(summary.glClosingBalance)}</span>
						</div>
						<div className="bank-recon-summary__row">
							<span>Add: Bank Interest</span>
							<span>{this.formatCurrency(summary.bankInterest)}</span>
						</div>
						<div className="bank-recon-summary__row">
							<span>Less: Bank Charges</span>
							<span>({this.formatCurrency(summary.bankCharges)})</span>
						</div>
						<div className="bank-recon-summary__row">
							<span>Less: NSF Fees</span>
							<span>({this.formatCurrency(summary.nsfFees)})</span>
						</div>
						<div className="bank-recon-summary__row bank-recon-summary__row--total">
							<span>Adjusted Book Balance</span>
							<span>{this.formatCurrency(summary.adjustedBookBalance)}</span>
						</div>
					</div>
				</div>

				{/* Difference */}
				<div className="bank-recon-summary">
					<div className="bank-recon-summary__card">
						<div className={`bank-recon-summary__row bank-recon-summary__row--difference ${diffClass}`}>
							<span>DIFFERENCE</span>
							<span>{this.formatCurrency(summary.difference)}</span>
						</div>
					</div>
				</div>

				{/* Download */}
				<div className="bank-recon-actions">
					<Button text="Download Excel Report" onClick={this.onDownloadClicked} />
				</div>
			</>
		);
	}

	renderItemsTable(items, title) {
		if (!items || items.length === 0) {
			return (
				<div className="bank-recon-items">
					<h3>{title}</h3>
					<p>No items found.</p>
				</div>
			);
		}

		return (
			<div className="bank-recon-items">
				<h3>{title}</h3>
				<table className="bank-recon-items__table">
					<thead>
						<tr>
							<th>Date</th>
							<th>Type</th>
							<th>Doc #</th>
							<th>Name</th>
							<th>Description</th>
							<th>Amount</th>
							<th>Category</th>
						</tr>
					</thead>
					<tbody>
						{items.map((item, index) => (
							<tr key={index}>
								<td>{item.date}</td>
								<td>{item.type}</td>
								<td>{item.docNum}</td>
								<td>{item.name}</td>
								<td>{item.description}</td>
								<td>{this.formatCurrency(item.amount, item.isoCurrencyCode)}</td>
								<td>
									<span className={`bank-recon-items__category bank-recon-items__category--${item.category}`}>
										{(item.category || "").replace(/_/g, " ")}
									</span>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		);
	}

	renderJournalEntries() {
		let { reconciliationResult } = this.state;
		let suggestions = reconciliationResult?.journalEntrySuggestions || [];

		if (suggestions.length === 0) {
			return (
				<div className="bank-recon-je">
					<h3>Suggested Adjusting Journal Entries</h3>
					<p>No journal entries to suggest.</p>
				</div>
			);
		}

		// Build a quick lookup so each JE can render its source transaction's currency
		const currencyByTxnId = new Map();
		(reconciliationResult.unmatchedBankItems || []).forEach(item => {
			if (item.qboTxnId) currencyByTxnId.set(item.qboTxnId, item.isoCurrencyCode);
		});

		return (
			<div className="bank-recon-je">
				<h3>Suggested Adjusting Journal Entries</h3>
				<table className="bank-recon-items__table">
					<thead>
						<tr>
							<th>Debit Account</th>
							<th>Credit Account</th>
							<th>Amount</th>
							<th>Memo</th>
							<th>Status</th>
							<th>Actions</th>
						</tr>
					</thead>
					<tbody>
						{suggestions.map((je, index) => {
							const sourceCurrency = je.sourceTxnId ? currencyByTxnId.get(je.sourceTxnId) : null;
							return (
							<tr key={je.id || index}>
								<td>{je.debitAccount}</td>
								<td>{je.creditAccount}</td>
								<td>{this.formatCurrency(je.amount, sourceCurrency)}</td>
								<td className="bank-recon-je__memo-cell">
									{this.state.editingMemoId === je.id ? (
										<div className="bank-recon-je__memo-edit">
											<input
												type="text"
												className="bank-recon-je__memo-input"
												value={this.state.editingMemoValue}
												onChange={this.onMemoEditChange}
												onKeyDown={e => this.onMemoKeyDown(e, je.id)}
												autoFocus
											/>
											<button className="bank-recon-je__btn bank-recon-je__btn--accept" onClick={() => this.onMemoEditSave(je.id)}>Save</button>
											<button className="bank-recon-je__btn bank-recon-je__btn--reject" onClick={this.onMemoEditCancel}>Cancel</button>
										</div>
									) : (
										<span className="bank-recon-je__memo-text" onClick={() => this.onMemoEditStart(je.id, je.memo)}>
											{je.memo || "—"}
											<span className="bank-recon-je__memo-edit-icon">&#9998;</span>
										</span>
									)}
								</td>
								<td>
									<span className={`bank-recon-je__status bank-recon-je__status--${je.status}`}>
										{je.status}
									</span>
								</td>
								<td>
									<div className="bank-recon-je__actions">
										{je.status === "suggested" && (
											<>
												<button
													className="bank-recon-je__btn bank-recon-je__btn--accept"
													onClick={() => this.onAcceptAdjustment(je.id)}
												>
													Accept
												</button>
												<button
													className="bank-recon-je__btn bank-recon-je__btn--reject"
													onClick={() => this.onRejectAdjustment(je.id)}
												>
													Reject
												</button>
											</>
										)}
										{je.status === "accepted" && (
											<button
												className="bank-recon-je__btn bank-recon-je__btn--post"
												onClick={() => this.onPostAdjustment(je.id)}
											>
												Post to QBO
											</button>
										)}
										{je.status === "posting" && (
											<span className="bank-recon-je__posting">Posting...</span>
										)}
										{je.status === "posted" && (
											<div className="bank-recon-je__posted-info">
												<span className="bank-recon-je__posted">Posted</span>
												{(je.postedDebitAccount || je.postedCreditAccount) && (
													<span className="bank-recon-je__posted-accounts">
														DR: {je.postedDebitAccount || je.debitAccount} / CR: {je.postedCreditAccount || je.creditAccount}
													</span>
												)}
											</div>
										)}
										{je.status === "rejected" && (
											<span className="bank-recon-je__rejected">Rejected</span>
										)}
									</div>
								</td>
							</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		);
	}

	renderHistory() {
		let { reconciliationHistory } = this.state;

		if (reconciliationHistory.length === 0) {
			return (
				<div className="bank-recon-history">
					<p>No reconciliation history found.</p>
				</div>
			);
		}

		return (
			<div className="bank-recon-history">
				{reconciliationHistory.map(recon => (
					<div key={recon.id} className="bank-recon-history__item" onClick={() => this.onHistoryItemClicked(recon.id)}>
						<div className="bank-recon-history__item-info">
							<span className="bank-recon-history__item-title">
								{recon.account_name || "Bank Account"} — {recon.period_start} to {recon.period_end}
							</span>
							<span className="bank-recon-history__item-date">
								Difference: {this.formatCurrency(recon.difference)} | Created: {moment(recon.created_at).format("MMM D, YYYY h:mm A")}
							</span>
						</div>
						<div className="bank-recon-history__item-badges">
							{recon.is_auto && (
								<span className="bank-recon-history__badge bank-recon-history__badge--auto">AUTO</span>
							)}
							{recon.needs_review && (
								<span className="bank-recon-history__badge bank-recon-history__badge--review">NEEDS REVIEW</span>
							)}
							<span className={`bank-recon-history__item-status bank-recon-history__item-status--${recon.status}`}>
								{recon.status}
							</span>
						</div>
					</div>
				))}
			</div>
		);
	}

	renderActiveTab() {
		let { activeTab, reconciliationResult } = this.state;

		if (activeTab === TABS.history) {
			return this.renderHistory();
		}

		if (!reconciliationResult) {
			return this.renderHistory();
		}

		switch (activeTab) {
			case TABS.summary:
				return this.renderSummary();
			case TABS.unmatchedBank:
				return this.renderItemsTable(reconciliationResult.unmatchedBankItems, "Bank-Only Items (Not in GL)");
			case TABS.unmatchedGL:
				return this.renderItemsTable(reconciliationResult.unmatchedGLItems, "GL-Only Items (Not on Bank Statement)");
			case TABS.journalEntries:
				return this.renderJournalEntries();
			case TABS.dataQuality:
				return (
					<DataQualityPanel
						clientId={this.props.clientId}
						plaidAccountId={this.state.selectedPlaidAccountId}
						duplicateGroups={this.state.duplicateGroups}
						alerts={this.state.dataQualityAlerts}
						foreignTransactions={this.state.foreignTransactions}
						feedHealth="healthy"
						onExclude={this.onDuplicateExcluded}
						onGroupResolved={this.onDuplicateGroupResolved}
						onConvertCurrencies={this.onConvertCurrencies}
					/>
				);
			default:
				return this.renderSummary();
		}
	}

	onDuplicateExcluded = txn => {
		// Instantly remove the excluded transaction from Bank-Only Items and any JE generated from it
		const { reconciliationResult } = this.state;
		if (!reconciliationResult) return;

		const txnKey = txn.qboTxnId || `${txn.date}-${txn.amount}-${txn.name}`;

		const unmatchedBankItems = (reconciliationResult.unmatchedBankItems || []).filter(item => {
			const itemKey = item.qboTxnId || `${item.date}-${item.amount}-${item.name}`;
			return itemKey !== txnKey;
		});

		const journalEntrySuggestions = (reconciliationResult.journalEntrySuggestions || []).filter(je => {
			if (je.sourceTxnId && txn.qboTxnId) return je.sourceTxnId !== txn.qboTxnId;
			return true;
		});

		this.update({
			reconciliationResult: {
				...reconciliationResult,
				unmatchedBankItems,
				journalEntrySuggestions,
			},
		});
	};

	onDuplicateGroupResolved = ({ groupId }) => {
		// Sync the parent-owned duplicateGroups so the tab badge + DQ banner counts update instantly
		const remaining = (this.state.duplicateGroups || []).filter(g => g.groupId !== groupId);
		this.update({ duplicateGroups: remaining });
	};

	onConvertCurrencies = currencies => {
		// Replace foreign-currency amounts with their CAD equivalents for the selected currencies.
		// Session-only: resets on a new reconciliation or page refresh.
		const { reconciliationResult, foreignTransactions } = this.state;
		if (!reconciliationResult || !currencies || currencies.length === 0) return;

		const toConvert = new Set(currencies);
		const convertedTxnIds = new Set();

		const convertItem = item => {
			if (
				item.isForeignCurrency &&
				item.isoCurrencyCode &&
				toConvert.has(item.isoCurrencyCode) &&
				item.cadAmount != null
			) {
				if (item.qboTxnId) convertedTxnIds.add(item.qboTxnId);
				return {
					...item,
					amount: parseFloat(item.cadAmount),
					isoCurrencyCode: "CAD",
					isForeignCurrency: false,
					convertedFromCurrency: item.isoCurrencyCode,
					convertedFromAmount: item.originalAmount,
				};
			}
			return item;
		};

		const unmatchedBankItems = (reconciliationResult.unmatchedBankItems || []).map(convertItem);

		// Recalculate JE amounts where the source txn was converted
		const originalAmountByTxn = new Map();
		(reconciliationResult.unmatchedBankItems || []).forEach(item => {
			if (item.qboTxnId) originalAmountByTxn.set(item.qboTxnId, item);
		});

		const journalEntrySuggestions = (reconciliationResult.journalEntrySuggestions || []).map(je => {
			if (je.sourceTxnId && convertedTxnIds.has(je.sourceTxnId)) {
				const srcItem = originalAmountByTxn.get(je.sourceTxnId);
				if (srcItem && srcItem.cadAmount != null) {
					return { ...je, amount: Math.abs(parseFloat(srcItem.cadAmount)) };
				}
			}
			return je;
		});

		// Recalculate summary totals using the converted items
		const summary = this._recalculateSummary({
			previousSummary: reconciliationResult.summary,
			unmatchedBankItems,
			unmatchedGLItems: reconciliationResult.unmatchedGLItems || [],
		});

		const remainingForeign = (foreignTransactions || []).filter(
			t => !toConvert.has(t.isoCurrencyCode)
		);

		this.update({
			reconciliationResult: {
				...reconciliationResult,
				unmatchedBankItems,
				journalEntrySuggestions,
				summary,
			},
			foreignTransactions: remainingForeign,
		});
	};

	_recalculateSummary = ({ previousSummary, unmatchedBankItems, unmatchedGLItems }) => {
		const sum = (arr, fn) => arr.reduce((s, x) => s + fn(x), 0);

		const outstandingDeposits = sum(
			unmatchedGLItems.filter(i => i.category === "outstanding_deposit"),
			i => parseFloat(i.amount) || 0
		);
		const outstandingCheques = sum(
			unmatchedGLItems.filter(i => i.category === "outstanding_cheque"),
			i => Math.abs(parseFloat(i.amount) || 0)
		);
		const bankInterest = sum(
			unmatchedBankItems.filter(i => i.category === "bank_interest"),
			i => parseFloat(i.amount) || 0
		);
		const bankCharges = sum(
			unmatchedBankItems.filter(i => i.category === "bank_charge"),
			i => Math.abs(parseFloat(i.amount) || 0)
		);
		const nsfFees = sum(
			unmatchedBankItems.filter(i => i.category === "nsf_fee"),
			i => Math.abs(parseFloat(i.amount) || 0)
		);

		const bankEndingBalance = previousSummary.bankEndingBalance;
		const glClosingBalance = previousSummary.glClosingBalance;
		const adjustedBankBalance = parseFloat(
			(bankEndingBalance + outstandingDeposits - outstandingCheques).toFixed(2)
		);
		const adjustedBookBalance = parseFloat(
			(glClosingBalance + bankInterest - bankCharges - nsfFees).toFixed(2)
		);
		const difference = parseFloat((adjustedBankBalance - adjustedBookBalance).toFixed(2));

		return {
			bankEndingBalance,
			outstandingDeposits: parseFloat(outstandingDeposits.toFixed(2)),
			outstandingCheques: parseFloat(outstandingCheques.toFixed(2)),
			adjustedBankBalance,
			glClosingBalance,
			bankInterest: parseFloat(bankInterest.toFixed(2)),
			bankCharges: parseFloat(bankCharges.toFixed(2)),
			nsfFees: parseFloat(nsfFees.toFixed(2)),
			adjustedBookBalance,
			difference,
		};
	};

	onDismissDqBanner = () => {
		this.update({ dqBannerDismissed: true });
	};

	onViewDataQuality = () => {
		this.update({ activeTab: TABS.dataQuality });
	};

	renderDqBanner() {
		let { reconciliationResult, duplicateGroups, dataQualityAlerts, foreignTransactions, dqBannerDismissed } = this.state;

		if (dqBannerDismissed || !reconciliationResult) return null;

		const activeAlerts = (dataQualityAlerts || []).filter(a => a.status !== "resolved");
		const foreignCount = foreignTransactions?.length || 0;
		const issueCount = (duplicateGroups?.length || 0) + activeAlerts.length + foreignCount;
		if (issueCount === 0) return null;

		return (
			<div className="dq-inline-banner">
				<div className="dq-inline-banner__body">
					<div className="dq-inline-banner__title">⚠ {issueCount} data quality issue{issueCount !== 1 ? "s" : ""} detected for this account</div>
					<ul className="dq-inline-banner__list">
						{duplicateGroups && duplicateGroups.length > 0 && (
							<li>{duplicateGroups.length} potential duplicate transaction{duplicateGroups.length !== 1 ? "s" : ""} found (review in Data Quality tab)</li>
						)}
						{foreignCount > 0 && (
							<li>{foreignCount} foreign currency transaction{foreignCount !== 1 ? "s" : ""} detected (review in Data Quality tab)</li>
						)}
						{activeAlerts.map(a => (
							<li key={a.id}>{a.message}</li>
						))}
					</ul>
				</div>
				<div className="dq-inline-banner__actions">
					<button className="dq-inline-banner__btn" onClick={this.onViewDataQuality}>View Data Quality</button>
					<button className="dq-btn dq-btn--ghost" onClick={this.onDismissDqBanner}>Dismiss</button>
				</div>
			</div>
		);
	}

	render() {
		let { loading, error } = this.state;

		return (
			<div className="container">
				<div className="container-header-title">
					<h1 className="container-header-title__name">Bank Reconciliation Tool</h1>
				</div>

				<div className="bank-recon-tool__body">
					{this.renderControls()}

					{this.renderDqBanner()}

					{error && <div style={{ color: "#c62828", fontSize: "13px", fontWeight: 600 }}>{error}</div>}

					{loading && (
						<div className="container-loader">
							<Loader />
						</div>
					)}

					{!loading && (
						<>
							{this.renderTabs()}
							{this.renderActiveTab()}
						</>
					)}
				</div>
			</div>
		);
	}
}

export default BankReconciliationTool;
